/**
 * Kling video family descriptors (Phase 1, U6): kling-2.5-turbo, kling-2.6, kling-3.0-standard /
 * kling-3.0-pro (one H4 family, `KLING_30_FAMILY`), kling-avatar.
 *
 * PURE: `import type` + sibling data only (purity.test). Importable from server/ and src/.
 *
 * P1 is price- and payload-neutral: every value below reproduces what the legacy arms send and
 * bill TODAY (server/routes/falai.ts `case 'kling-2.6'/'kling-2.5-turbo'`, `case 'kling-3.0-*'`,
 * `case 'kling-avatar'`, the resolution guard after the switch; ProviderService.generateVideoFalAI
 * Kling branches and headlessFalVideoSeconds). Quirks are kept on purpose and named in `legacy.*`.
 *
 * Shared legacy facts for the whole family (all verified against U0's goldens @9089a33):
 *   - PRICE. Every Kling row is `flat_rate = false`, so CreditService's generic tail bills
 *     ceil(base x ceil(units) x ladder(resolution)): 480p bills 0.75x (and renders 720p, Kling has
 *     no resolution field). The rate is therefore the legacy ladder, not a flat number.
 *   - RESOLUTION GUARD (falai.ts, after the switch). On the EDITOR a tier whose ladder multiplier
 *     is > 1 (1080p, 1440p, 4k, 2160p ...) is reserved, then refused with a 500 and refunded,
 *     because the Kling payload carries no resolution. The descriptor refuses those values
 *     pre-reserve instead (class B1). HEADLESS has no such guard: it bills the ladder and renders
 *     720p, so the headless policy is 'passthrough' (R10).
 *   - UNITS. Legacy reserves the RAW body duration ('raw' stage), including garbage.
 *
 * R5 — THE ONE `default` RULE FOR THIS BATCH (kling-video, seedance; review round 1; veo-fal left at p1/integration-6).
 * `default` is what LEGACY resolves for an absent or blank body duration on the EDITOR surface:
 * the route's own destructuring fallback `duration = 5` (falai.ts:1197), whenever that value is
 * inside the model's domain. It has to be stated, because the two surfaces disagree — the
 * headless mapper resolves an absent duration to `Number(duration) || 8`
 * (ProviderService.ts:1209) — and a Param carries ONE scalar default, so no value can reproduce
 * both. NEITHER real path consults it: both legacy mappers substitute their own fallback before
 * the engine sees the request (editor 5, headless 8), so the default is reachable only through
 * the blank rows and through the pricing harness's synthetic `units: undefined` axis. (fal's VEO 3.1
 * was the single exception, 5 not being a VEO length; it was retired on staging, 2860433.)
 */
import type { ModelDescriptor, ModelFamily, Param, Sku } from '../types';
import { IMG, END, W_IMG, W_END } from '../_shared';

const VERIFIED = '2026-09-19';

/**
 * The legacy ladder tiers that bill <= 1x, i.e. the ones the editor guard lets through
 * (RESOLUTION_MULTIPLIERS: 480p 0.75, 580p 1, 720p 1).
 *
 * LADDER_GUARD — CLOSED at worker/p1-b3-kvs by `domainRules` (see the param below). The
 * history, kept because the aliases below predate it: the legacy guard refuses
 * by PREDICATE: `legacyResolutionMultiplier(r) > 1`. Its accepting set is CO-finite, because
 * getResolutionTier falls through to '720p' for anything it does not recognise — so legacy
 * accepts '1280x720', '512', a bare '480' *and* 'garbage', all at <= 1x. A descriptor domain is
 * finite and cannot express that. The `aliases` below cover every NAMED spelling the ladder maps
 * onto a <= 1x tier. Before U1-VOCAB an UNRECOGNISED string (which legacy bills at 1x and
 * renders at 720p) was refused pre-reserve on the editor; the editor `domainRules` entry now
 * states the guard's own predicate, so the accepting set is co-finite again and 'garbage' (the
 * one such value in the U0 edge domain) bills and renders exactly as legacy does.
 */
const kLingResolution = (label = 'Resolution'): Param => ({
  id: 'resolution', type: 'enum', values: ['480p', '580p', '720p'], uiValues: ['720p'], default: '720p',
  normalize: 'lower',
  aliases: {
    // -> 480p (0.75x): getResolutionTier matches on `includes('480')`
    '480': '480p', '640x480': '480p', '854x480': '480p', '480x854': '480p',
    // -> 580p (1x): `includes('580')`
    '580': '580p',
    // -> 720p (1x): `includes('720')`, the 1280x720 pair, the '512' image rung, and the
    // unrecognised-string fallthrough spellings a Kling node can actually hold.
    '720': '720p', '1280x720': '720p', '720x1280': '720p', '512': '720p', '512px': '720p',
    '768p': '720p', '2k': '720p',
  },
  role: 'tier',
  // editor: the post-reserve 500 (falai.ts resolution guard) becomes a pre-reserve 400 (B1).
  // headless: no guard; the raw value reaches the ladder exactly as ProviderService bills it.
  invalid: { editor: 'reject', headless: 'passthrough' },
  // LADDER_GUARD, closed with U1-VOCAB (4). The editor guard is a PREDICATE, not a list: it
  // refuses `legacyResolutionMultiplier(r) > 1` and serves everything else. This rule is that
  // predicate's complement over the SAME shared ladder function (models/pricing.ts), so any
  // string the route serves ('garbage', '1280x720', '480x640' ...) is served here too, and is
  // priced from the raw value exactly as CreditService prices it (wire: false — Kling has no
  // resolution field, fal renders 720p either way). A value over 1x still falls through to
  // `invalid.editor: 'reject'` (B1). Headless needs no rule: it is passthrough already.
  domainRules: {
    editor: [{
      when: { ladder: { curve: 'legacy-resolution-v1', lte: 1 } },
      then: 'passthrough',
      why: 'falai.ts Kling resolution guard serves every value whose ladder multiplier is <= 1 and bills it from the raw string',
    }],
  },
  wire: false,
  ui: { label, tier: 'hidden' },
});

/** The route sanitizes aspectRatio (VALID_ASPECT_RATIOS) and forwards it unvalidated. */
const klingAspect = (): Param => ({
  id: 'aspectRatio', type: 'enum', values: ['16:9', '9:16', '1:1'], default: '16:9', onlyModes: ['t2v'],
  invalid: 'passthrough', wire: { field: 'aspect_ratio' }, ui: { label: 'Aspect ratio', tier: 'basic' },
});

/**
 * `operationType` is COPIED FROM THE LIVE ROW, never chosen (types.ts). Every falai video row in
 * the production dump says 'video' EXCEPT the four kling-3.0 ones, which say 'video_generation'
 * — real drift in model_registry, flagged by the projection run at integration-2. P1 records
 * what is there so migration 144 changes nothing; normalising those four rows is a separate,
 * owner-approved migration.
 */
const ladderSku = (
  id: string, base: number, usd: number, displayName: string,
  extra: Partial<Sku> = {}, operationType = 'video',
): Sku => ({
  id,
  rate: { ladder: { base, by: 'resolution', curve: 'legacy-resolution-v1' } },
  vendor: { usd, per: 'second', field: 'pricingInfoOverride', source: 'fal.ai model page pricingInfoOverride', verified: VERIFIED },
  registry: { displayName, operationType, publishedTier: '720p', flatRate: false },
  ...extra,
});

// ─── kling-2.5-turbo ─────────────────────────────────────────────────────────
export const KLING_25_TURBO: ModelDescriptor = {
  id: 'kling-2.5-turbo', provider: 'falai', face: 'video', displayName: 'Kling 2.5 Turbo', ledgerName: 'Kling 2.5 Turbo',
  status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video', ports: [], media: {} },
    i2v: { endpoint: 'fal-ai/kling-video/v2.5-turbo/pro/image-to-video', ports: [IMG()], media: { image: W_IMG } },
  },
  params: [
    { id: 'duration', type: 'int', values: [5, 10], uiValues: [5, 10], default: 5, normalize: 'trim', role: 'units',
      // editor: 400 before reserving (falai.ts "Unsupported duration"); headless: headlessFalVideoSeconds throws.
      invalid: 'reject', wire: { field: 'duration', as: 'string' }, ui: { label: 'Duration', tier: 'basic' } },
    klingAspect(),
    kLingResolution(),
  ],
  hooks: { payload: 'kling26StartImageUrl' },   // the shared kling-2.6 / 2.5-turbo arm: t2v aspect_ratio only when isTextToVideo
  legacy: {
    wire: [
      { field: 'cfg_scale', value: 0.5, surface: 'editor', why: 'editor arm constant (fal default is also 0.5); headless never sends it',
        retire: 'inert-cleanup' },
    ],
    notes: [
      'Editor first-last-frame with two images is refused 400 before reserving (falai.ts, 2860433: the VEO-shaped branch that posted it to the T2V endpoint was retired with the fal.ai VEO 3.1 model); the engine refuses it in resolve() (no flf mode, UNSUPPORTED_MODE).',
      'Editor t2v endpoint reached WITHOUT isTextToVideo (i2v+lastImage, FLF with one image, garbage mode) sends no aspect_ratio: reproduced by the kling26StartImageUrl hook.',
    ],
  },
  pricing: {
    unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'generic', policy: 'fal-video-35',
    skus: [ladderSku('kling-2.5-turbo', 32, 0.07, 'Kling 2.5 Turbo', {
      vendor: { usd: 0.07, per: 'second', field: 'pricingInfoOverride', source: 'fal-ai/kling-video/v2.5-turbo/pro/text-to-video',
        text: 'For **5s** video your request will cost **$0.35**. For every aditional second you will be charged **$0.07.**', verified: VERIFIED },
      marginException: 'live row bills 32 cr/s; fal-video-35 policy for $0.07/s is 33 (kept: P1 is price-neutral)',
    })],
  },
  knowledge: { summary: 'Kling 2.5 Turbo Pro: 5 or 10 second clips, no audio. aspect_ratio on text-to-video only.' },
  schemaVerified: VERIFIED,
};

// ─── kling-2.6 ───────────────────────────────────────────────────────────────
export const KLING_26: ModelDescriptor = {
  id: 'kling-2.6', provider: 'falai', face: 'video', displayName: 'Kling 2.6 Pro', ledgerName: 'Kling 2.6', status: 'live',
  prompt: { field: 'prompt', required: true },
  modes: {
    t2v: { endpoint: 'fal-ai/kling-video/v2.6/pro/text-to-video', ports: [], media: {} },
    // v2.6 image-to-video REQUIRES start_image_url (it has no image_url) and has no cfg_scale.
    i2v: { endpoint: 'fal-ai/kling-video/v2.6/pro/image-to-video', ports: [IMG()],
           media: { image: { field: 'start_image_url', encode: 'image' } } },
  },
  params: [
    { id: 'duration', type: 'int', values: [5, 10], uiValues: [5, 10], default: 5, normalize: 'trim', role: 'units',
      invalid: 'reject', wire: { field: 'duration', as: 'string' }, ui: { label: 'Duration', tier: 'basic' } },
    klingAspect(),
    // generate_audio DEFAULTS TO TRUE on fal: legacy always sends !!enableAudio (editor arm; headless
    // applyHeadlessLegacyWire). Truthiness, not strict true, selects the -audio SKU (falVideoCreditModelId).
    { id: 'enableAudio', type: 'bool', default: false, role: 'sku', invalid: 'passthrough',
      wire: { field: 'generate_audio', as: 'boolean' }, ui: { label: 'Generate audio', tier: 'basic' } },
    kLingResolution(),
  ],
  hooks: { payload: 'kling26StartImageUrl' },
  legacy: {
    wire: [
      { field: 'cfg_scale', value: 0.5, modes: ['t2v'], surface: 'editor',
        why: 'editor arm constant on text-to-video (fal default 0.5); deleted on i2v; headless never sends it', retire: 'inert-cleanup' },
    ],
    notes: [
      'Editor first-last-frame with two images is refused 400 before reserving (falai.ts, 2860433: the VEO-shaped branch that posted it to the T2V endpoint was retired with the fal.ai VEO 3.1 model); the engine refuses it in resolve() (no flf mode, UNSUPPORTED_MODE).',
    ],
  },
  pricing: {
    unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'generic', policy: 'fal-video-35',
    skus: [
      ladderSku('kling-2.6-audio', 56, 0.14, 'Kling 2.6 Pro (Audio)', {
        when: { enableAudio: { neq: false } },
        // The two v2.6 pages carry DIFFERENT pricingInfoOverride text (both re-read 2026-09-19).
        // image-to-video advertises a THIRD tier at $0.168/s ("native audio and voice control")
        // that the t2v page does not mention. Legacy exposes no voice-control switch, so nothing
        // can reach it and P1 is unaffected — but it is recorded here so a future reprice sees it
        // (decisionsForOwner).
        vendor: { usd: 0.14, per: 'second', field: 'pricingInfoOverride',
          source: 'fal-ai/kling-video/v2.6/pro/text-to-video (+ .../image-to-video, fuller text)',
          text: 't2v: "For every second of video you generated, you will be charged $0.07 (audio off) or $0.14 (audio on). For example, a 5s video with audio on will cost $0.70" | i2v: "Videos generated without audio cost $0.07 per second. Videos with native audio cost $0.14 per second, and videos with native audio and voice control cost $0.168 per second. For example, a 5-second video with audio costs $0.70, while a 10-second video with audio and voice control costs $1.68"',
          verified: VERIFIED },
        marginException: 'live row bills 56 cr/s; policy for $0.14/s is 65 (a kling-2.6-pro-audio row at 65 exists but is not the reserved id). P1 is price-neutral.',
      }),
      ladderSku('kling-2.6', 28, 0.07, 'Kling 2.6 Pro', {
        vendor: { usd: 0.07, per: 'second', field: 'pricingInfoOverride', source: 'fal-ai/kling-video/v2.6/pro/text-to-video',
          text: 'For every second of video you generated, you will be charged $0.07 (audio off) or $0.14 (audio on). For example, a 5s video with audio on will cost $0.70', verified: VERIFIED },
        marginException: 'live row bills 28 cr/s; policy for $0.07/s is 33 (a kling-2.6-pro row at 32 exists but is not the reserved id). P1 is price-neutral.',
      }),
    ],
  },
  knowledge: { summary: 'Kling 2.6 Pro: 5 or 10 second clips with optional native audio (billed as the -audio SKU).' },
  schemaVerified: VERIFIED,
};

// ─── kling-3.0 standard / pro (H4 family) ────────────────────────────────────
const kling30 = (tier: 'standard' | 'pro', c: { base: number; audio: number }, usd: { base: number; audio: number },
  text: string): ModelDescriptor => {
  const id = `kling-3.0-${tier}`;
  const name = tier === 'pro' ? 'Kling 3.0 Pro' : 'Kling 3.0 Standard';
  const slug = (m: string) => `fal-ai/kling-video/o3/${tier}/${m}`;
  const vendor = (u: number) => ({ usd: u, per: 'second' as const, field: 'pricingInfoOverride' as const, source: slug('text-to-video'), text, verified: VERIFIED });
  return {
    id, provider: 'falai', face: 'video', displayName: name, ledgerName: name, status: 'live',
    family: { id: 'kling-3.0', label: 'Kling 3.0', axis: 'quality', value: tier },
    prompt: { field: 'prompt', required: false },
    modes: {
      t2v: { endpoint: slug('text-to-video'), ports: [], media: {} },
      i2v: { endpoint: slug('image-to-video'), ports: [IMG()], media: { image: W_IMG } },
      // No dedicated FLF endpoint: image-to-video with end_image_url (falai.ts isI2vEndpointFlfModel).
      flf: { endpoint: slug('image-to-video'), ports: [IMG('input-image-first'), END()], media: { image: W_IMG, endImage: W_END } },
    },
    params: [
      { id: 'duration', type: 'enum', values: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'],
        uiValues: ['3', '5', '10', '15'], default: '5', role: 'units',   // R5 batch rule: the route fallback (see the header)
        // editor: String(duration) of whatever arrived (no validation); headless: headlessFalVideoSeconds rejects.
        invalid: { editor: 'passthrough', headless: 'reject' },
        wire: { field: 'duration', as: 'string' }, ui: { label: 'Duration', tier: 'basic' } },
      klingAspect(),
      // `if (enableAudio && supportsAudio) generate_audio = true`: truthiness, sent only when on (editor).
      // Headless always sends !!enableAudio (applyHeadlessLegacyWire, audio-priced model).
      { id: 'enableAudio', type: 'bool', default: false, role: 'sku', invalid: 'passthrough',
        sendWhen: { enableAudio: { neq: false } }, wire: { field: 'generate_audio', as: 'boolean' },
        ui: { label: 'Generate audio', tier: 'basic' } },
      kLingResolution(),
    ],
    legacy: {
      wire: [{ field: 'aspect_ratio', fromParam: 'aspectRatio', modes: ['i2v', 'flf'], surface: 'editor',
               why: 'absent from the o3 image-to-video schema (oracle 2026-09-19); the editor arm sends it in every mode', retire: 'inert-cleanup' }],
      omits: [{ field: 'generate_audio', why: 'editor omits it when audio is off; fal default is false, the value priced' }],
      notes: [
        `The KlingO3Video node's own duration default is '3'; the descriptor default is the route fallback '5' (R5 batch rule, see the header). A P2 picker that preselects \`default\` must read the node default from the family, not from here.`,
      ],
    },
    pricing: {
      unit: 'second', units: { param: 'duration', stage: 'raw' }, rounding: 'generic', policy: 'fal-video-35',
      skus: [
        // 'video_generation' is what the LIVE kling-3.0 rows carry — see ladderSku's header.
        ladderSku(`${id}-audio`, c.audio, usd.audio, `${name} (Audio)`, { when: { enableAudio: { neq: false } }, vendor: vendor(usd.audio) }, 'video_generation'),
        ladderSku(id, c.base, usd.base, name, { vendor: vendor(usd.base) }, 'video_generation'),
      ],
    },
    knowledge: { summary: `${name}: 3-15 s, optional native audio (the -audio SKU), first-last-frame via end_image_url.` },
    schemaVerified: VERIFIED,
  };
};

export const KLING_30_STANDARD = kling30('standard', { base: 39, audio: 52 }, { base: 0.084, audio: 0.112 },
  'For every second of video you generated, you will be charged **$0.084** (audio off) or **$0.112** (audio on). For example, a 5s video with audio on will cost **$0.56** ');
export const KLING_30_PRO = kling30('pro', { base: 52, audio: 65 }, { base: 0.112, audio: 0.14 },
  'For every second of video you generated, you will be charged **$0.112** (audio off) or **$0.14** (audio on). For example, a 5s video with audio on will cost **$0.70**');

/**
 * Kling 3.0 as ONE picker entry (plan H4). The legacy KlingO3Video node ships
 * `selectedModel: ''` on purpose (CLAUDE.md): its model derives from `quality`, and a truthy
 * selectedModel would beat the quality map. The sentinel is preserved, not "fixed".
 */
export const KLING_30_FAMILY: ModelFamily = {
  id: 'kling-3.0', label: 'Kling 3.0', face: 'video',
  variant: { param: 'quality', label: 'Quality', default: 'standard',
    options: [
      { value: 'standard', label: 'Standard', model: 'kling-3.0-standard' },
      { value: 'pro', label: 'Pro', model: 'kling-3.0-pro' },
    ] },
  legacyNodes: [{ nodeType: 'KlingO3Video', selectedModelSentinel: '' }],
};

// ─── kling-avatar ────────────────────────────────────────────────────────────
const AVATAR_ENDPOINT = 'fal-ai/kling-video/ai-avatar/v2/standard';
const W_AUDIO = { field: 'audio_url', encode: 'raw' } as const;   // editor: convertToBase64DataUrl (not the image optimizer)
const AUDIO_PORT = { slot: 'audio', kind: 'audio', min: 1, max: 1, handle: 'input-audio' } as const;
const avatarMode = () => ({ endpoint: AVATAR_ENDPOINT, ports: [IMG(), AUDIO_PORT], media: { image: W_IMG, audio: W_AUDIO } });

export const KLING_AVATAR: ModelDescriptor = {
  id: 'kling-avatar', provider: 'falai', face: 'video', displayName: 'Kling Avatar', ledgerName: 'Kling Avatar', status: 'live',
  // Schema is exactly { image_url (req), audio_url (req), prompt (opt) }. The route validated a prompt.
  prompt: { field: 'prompt', required: false },
  // One endpoint for every mode. The legacy route decides the mode before the arm runs, so i2v and
  // t2v are both declared (same endpoint, same required ports); lipsync is the P2 smart-mode name.
  modes: { i2v: avatarMode(), t2v: avatarMode(), lipsync: avatarMode() },
  params: [kLingResolution()],
  legacy: {
    notes: [
      'No duration on the wire: the clip is as long as the audio. Billed units are the caller-stated audio length (media.audioSeconds, from body.duration).',
      'Headless refuses before reserving (no audio input on the headless API): reproduced by the required audio port.',
    ],
  },
  pricing: {
    unit: 'second', units: { media: 'audioSeconds' }, rounding: 'generic', policy: 'fal-video-35',
    skus: [ladderSku('kling-avatar', 26, 0.0562, 'Kling Avatar', {
      vendor: { usd: 0.0562, per: 'second', field: 'sidebar', source: 'fal.ai/models/fal-ai/kling-video/ai-avatar/v2/standard (pricingInfoOverride undefined; generic-template sidebar)',
        text: '$0.0562/second', verified: VERIFIED },
    })],
  },
  knowledge: { summary: 'Lip-sync a portrait to an audio track. Needs one image and one audio; billed per second of audio.' },
  schemaVerified: VERIFIED,
};

export const KLING_VIDEO_MODELS: readonly ModelDescriptor[] = [KLING_25_TURBO, KLING_26, KLING_30_STANDARD, KLING_30_PRO, KLING_AVATAR];
export const KLING_VIDEO_FAMILIES: readonly ModelFamily[] = [KLING_30_FAMILY];
